import * as React from "react";
import type { Namespace } from "@ewn/shared";
import { saveTranslations } from "@/api/admin";
import { useAction } from "@/lib/useAsync";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/modal";
import {
  fileName,
  planImport,
  readFile,
  toFile,
  type ImportPlan,
  type Strings,
} from "./translation-file";

/** Hands the browser a file to save. */
function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  // After the click has been handled: revoked at once, some browsers cancel.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A file's text. `FileReader`, which every browser and the test DOM have. */
export function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** How many keys an import names before it says "and N more". */
const LISTED = 10;

/**
 * One language's strings out to a file and back in.
 *
 * The export is what is SAVED, not what is typed. The import writes only the
 * keys it adds or changes, as the editor's own save does, so a colleague's
 * edit to a key the file leaves alone survives; keys missing from the file
 * are kept, not deleted. It waits while the editor holds unsaved changes,
 * which the import would otherwise land underneath, and while the strings of
 * the language on screen have not arrived (`strings` is null).
 */
export function TranslationTransfer({
  locale,
  namespace,
  strings,
  busy,
  onImported,
}: {
  locale: string;
  namespace: Namespace;
  /** The saved strings, as last read. */
  strings: Strings | null;
  /** True while the editor holds changes or is saving them. */
  busy: boolean;
  onImported: () => void;
}) {
  const picker = React.useRef<HTMLInputElement>(null);
  const [pending, setPending] = React.useState<{ name: string; plan: ImportPlan } | null>(
    null,
  );
  const [problem, setProblem] = React.useState<string | null>(null);
  const action = useAction();

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Cleared, so choosing the same file again is a change too.
    e.target.value = "";
    if (!file || !strings) return;
    let text: string;
    try {
      text = await readText(file);
    } catch {
      setProblem("That file could not be read.");
      return;
    }
    const read = readFile(text);
    setProblem(read.ok ? null : read.message);
    if (read.ok) setPending({ name: file.name, plan: planImport(strings, read.strings) });
  }

  async function write(writes: Strings) {
    if (await action.run(() => saveTranslations(locale, namespace, writes))) {
      setPending(null);
      onImported();
    }
  }

  const writeCount = pending ? Object.keys(pending.plan.writes).length : 0;
  function close() {
    // Not while writing: closed then, a failed write has nowhere to say so.
    if (action.busy) return;
    setPending(null);
    action.reset();
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={!strings}
        onClick={() => strings && download(fileName(namespace, locale), toFile(strings))}
      >
        Export JSON
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!strings || busy}
        title={busy ? "Save or discard the changes here first" : undefined}
        onClick={() => picker.current?.click()}
      >
        Import JSON
      </Button>
      <input
        ref={picker}
        type="file"
        accept="application/json,.json"
        hidden
        aria-label="Translation file to import"
        onChange={(e) => void pick(e)}
      />
      {problem && (
        <span role="alert" className="self-center text-xs text-destructive">
          {problem}
        </span>
      )}

      <Modal
        open={pending !== null}
        title={`Import ${pending?.name ?? ""}`}
        onClose={close}
        footer={
          <>
            <Button variant="outline" onClick={close} disabled={action.busy}>
              Cancel
            </Button>
            <Button
              disabled={action.busy || writeCount === 0}
              onClick={() => pending && void write(pending.plan.writes)}
            >
              {action.busy ? "Importing…" : `Import ${writeCount}`}
            </Button>
          </>
        }
      >
        {pending && (
          <div className="space-y-3 text-sm">
            {action.error && <Notice tone="error">{action.error}</Notice>}
            <p>
              Into {locale} · {namespace}: {pending.plan.added.length} new,{" "}
              {pending.plan.changed.length} changed, {pending.plan.same} unchanged. Keys the file
              leaves out are kept.
            </p>
            {pending.plan.changed.length > 0 && (
              <KeyList title="Changed" keys={pending.plan.changed} />
            )}
            {pending.plan.added.length > 0 && <KeyList title="New" keys={pending.plan.added} />}
          </div>
        )}
      </Modal>
    </>
  );
}

function KeyList({ title, keys }: { title: string; keys: string[] }) {
  const more = keys.length - LISTED;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{title}</div>
      <ul className="font-mono text-xs">
        {keys.slice(0, LISTED).map((key) => (
          <li key={key}>{key}</li>
        ))}
        {more > 0 && <li className="text-muted-foreground">and {more} more</li>}
      </ul>
    </div>
  );
}
